import { API_BASE_URL } from '../config/api';

// Types for authentication
export interface User {
  id: number;
  email: string;
  username?: string;
  display_name?: string;
  tenant?: string;
  google_id?: string;
  auth_provider?: 'local' | 'google' | 'both';
  profile_picture_url?: string;
  email_verified?: boolean;
  created_at?: string;
  updated_at?: string;
  module_configs?: string[];
  show_debug_icon?: boolean;
  show_floating_chat?: boolean;
  show_quick_command?: boolean;
  days_section_configs?: DaysSectionConfig;
  birth_date?: string;
  life_expectancy?: number;
}

export interface DaysSectionConfig {
  habits?: boolean;
  tasks?: boolean;
  goals?: boolean;
  schedule?: boolean;
  onGoingWorks?: {
    enabled?: boolean;
    novels?: boolean;
    knowledge?: boolean;
    books?: boolean;
    essays?: boolean;
  };
}

export interface AuthResponse {
  success: boolean;
  message: string;
  data?: {
    user: User;
    token: string;
    expiresIn: string;
    isNewUser?: boolean;
  };
  error?: string;
  details?: string[];
}

export interface RegisterData {
  email: string;
  password: string;
  username?: string;
  display_name?: string;
}

export interface LoginData {
  email: string;
  password: string;
}

export interface GoogleAuthData {
  credential: string;
}

// Token management
const TOKEN_KEY = 'authToken';

export const getStoredToken = (): string | null => {
  return localStorage.getItem(TOKEN_KEY);
};

export const setStoredToken = (token: string): void => {
  localStorage.setItem(TOKEN_KEY, token);
};

export const removeStoredToken = (): void => {
  localStorage.removeItem(TOKEN_KEY);
};

// Helper function to make authenticated requests
export const makeAuthenticatedRequest = async (url: string, options: RequestInit = {}): Promise<any> => {
  const token = getStoredToken();
  
  const response = await fetch(`${API_BASE_URL}${url}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers
    }
  });

  if (!response.ok) {
    if (response.status === 401) {
      // Token expired or invalid, remove it
      removeStoredToken();
      throw new Error('Authentication expired. Please log in again.');
    }
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
  }

  return response.json();
};

// Authentication API calls
export const register = async (userData: RegisterData): Promise<AuthResponse> => {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(userData)
    });

    const data = await response.json();
    
    if (data.success && data.data?.token) {
      setStoredToken(data.data.token);
    }
    
    return data;
  } catch (error) {
    console.error('Registration error:', error);
    throw error;
  }
};

export const login = async (loginData: LoginData): Promise<AuthResponse> => {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(loginData)
    });

    const data = await response.json();
    console.log('Login data:', data);
    
    if (data.success && data.data?.token) {
      setStoredToken(data.data.token);
    }
    
    return data;
  } catch (error) {
    console.error('Login error:', error);
    throw error;
  }
};

export const googleAuth = async (googleData: GoogleAuthData): Promise<AuthResponse> => {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/google`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(googleData)
    });

    const data = await response.json();
    console.log('Google auth data:', data);
    
    if (data.success && data.data?.token) {
      setStoredToken(data.data.token);
    }
    
    return data;
  } catch (error) {
    console.error('Google auth error:', error);
    throw error;
  }
};

export const logout = async (): Promise<void> => {
  removeStoredToken();
};

export const getProfile = async (): Promise<User> => {
  const response = await makeAuthenticatedRequest('/auth/profile');
  return response.data.user;
};

export const updateProfile = async (profileData: { username?: string; display_name?: string }): Promise<User> => {
  const response = await makeAuthenticatedRequest('/auth/profile', {
    method: 'PUT',
    body: JSON.stringify(profileData)
  });
  return response.data.user;
};

export const changePassword = async (passwordData: { current_password: string; new_password: string }): Promise<void> => {
  await makeAuthenticatedRequest('/auth/change-password', {
    method: 'PUT',
    body: JSON.stringify(passwordData)
  });
};

export const getModuleConfigs = async (): Promise<string[]> => {
  const user = await getProfile();
  return user.module_configs || [];
};

export const updateModuleConfigs = async (moduleIds: string[]): Promise<User> => {
  const response = await makeAuthenticatedRequest('/auth/module-configs', {
    method: 'PUT',
    body: JSON.stringify({ module_configs: moduleIds })
  });
  return response.data.user;
};

export const updateDebugIconPreference = async (showDebugIcon: boolean): Promise<User> => {
  const response = await makeAuthenticatedRequest('/auth/debug-icon-preference', {
    method: 'PUT',
    body: JSON.stringify({ show_debug_icon: showDebugIcon })
  });
  return response.data.user;
};

export const updateFloatingChatPreference = async (showFloatingChat: boolean): Promise<User> => {
  const response = await makeAuthenticatedRequest('/auth/floating-chat-preference', {
    method: 'PUT',
    body: JSON.stringify({ show_floating_chat: showFloatingChat })
  });
  return response.data.user;
};

export const updateQuickCommandPreference = async (showQuickCommand: boolean): Promise<User> => {
  const response = await makeAuthenticatedRequest('/auth/quick-command-preference', {
    method: 'PUT',
    body: JSON.stringify({ show_quick_command: showQuickCommand })
  });
  return response.data.user;
};

export const getDaysSectionConfigs = async (): Promise<DaysSectionConfig | null> => {
  const user = await getProfile();
  return user.days_section_configs || null;
};

export const updateDaysSectionConfigs = async (config: DaysSectionConfig): Promise<User> => {
  const response = await makeAuthenticatedRequest('/auth/days-section-configs', {
    method: 'PUT',
    body: JSON.stringify({ days_section_configs: config })
  });
  return response.data.user;
};

export const updateBirthDate = async (birthDate: string | null): Promise<User> => {
  const response = await makeAuthenticatedRequest('/auth/birth-date', {
    method: 'PUT',
    body: JSON.stringify({ birth_date: birthDate })
  });
  return response.data.user;
};

export const updateLifeExpectancy = async (lifeExpectancy: number | null): Promise<User> => {
  const response = await makeAuthenticatedRequest('/auth/life-expectancy', {
    method: 'PUT',
    body: JSON.stringify({ life_expectancy: lifeExpectancy })
  });
  return response.data.user;
};

// Check if user is authenticated
export const isAuthenticated = (): boolean => {
  return !!getStoredToken();
};